/**
 * Floating UI positioning system
 * Provides tooltip and popover positioning using the Floating UI library
 * Handles auto-placement, collision detection, and responsive positioning
 *
 * Floating-ui (popper.js successor library): https://floating-ui.com/docs/tutorial
 */
(async () => {
  // @floating-ui/dom imports won't work without a NODE_ENV defined
  // TODO: maybe check for a better solution to fix this (with rollup).
  // CURRENT USAGE: Uncomment while developing.
  window.process = {
    env: {
      NODE_ENV: 'development',
    }
  };

  const { 
    computePosition,
    autoPlacement,
    shift,
    limitShift,
    offset,
    hide
  } = await import('@floating-ui/dom');

  class FloatingUI extends HTMLElement {
    constructor() {
      super();
  
      // Elements
      this.trigger = this.querySelector('[data-floating-trigger]');
      this.target = this.querySelector('[data-floating-target]');
  
      if (!this.trigger || !this.target) {
        console.error('FloatingUI: Trigger or target element not found.');
        return;
      }
  
      this.references = {
        focus: (e) => this.show(e),
        blur: (e) => this.hide(e),
        mouseEnter: (e) => this.show(e),
        mouseLeave: (e) => this.hide(e),
        scroll: (e) => this.hideOnScroll(e),
      };
  
      this.middleware = [
        offset(10),
        shift({
          limiter: limitShift()
        }),
        autoPlacement(),
        hide()
      ];
  
      [
        ['mouseenter', this.references.mouseEnter],
        ['mouseleave', this.references.mouseLeave],
        ['focus', this.references.focus],
        ['blur', this.references.blur],
      ].forEach(([event, listener]) => {
        this.trigger.addEventListener(event, listener.bind(this));
      });
    }
  
    update() {
      if (!this.trigger || !this.target) return;
  
      computePosition(this.trigger, this.target, {
        strategy: 'fixed',
        middleware: this.middleware,
      }).then(({x, y, middlewareData}) => {
        Object.assign(this.target.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    }
  
    show(e) {
      if (!this.target) return;
  
      this.target.setAttribute('aria-hidden', 'false');
      this.update();
  
      window.addEventListener('scroll', this.references.scroll);
    }
  
    hide(e) {
      if (!this.target || !this.trigger) return;
  
      this.target.setAttribute('aria-hidden', 'true');
      this.trigger.blur();
    }
  
    hideOnScroll() {
      this.hide();
      window.removeEventListener('scroll', this.references.scroll);
    }
  }

  window.FloatingUI = FloatingUI;
  customElements.define('floating-ui', FloatingUI);

})();
